import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download, Loader2, Printer } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import {
  MISC_CUPS_PRINTERS_QUERY_KEY,
  QUALITY_CASE_CUPS_PRINTER_STORAGE_KEY,
  cupsPrintersForDocumentPrint,
  fetchMiscCupsPrinters,
  formatCupsPrinterOptionLabel,
  pickQualityCaseCupsPrinter,
} from "@/lib/miscPrintingApi";

import {
  fetchQualityCaseTravelerHtml,
  printQualityCaseTraveler,
} from "./casesApi";

function downloadHtml(html: string, filename: string): void {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function QualityInvestigationTravelerPrintControls({
  caseId,
  investigationId,
}: {
  caseId: number;
  investigationId?: number | null;
}): JSX.Element {
  const { showToast } = useHubToast();
  const [open, setOpen] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState("");

  const printersQuery = useQuery({
    queryKey: MISC_CUPS_PRINTERS_QUERY_KEY,
    queryFn: fetchMiscCupsPrinters,
    staleTime: 60_000,
    enabled: open,
  });

  const printers = useMemo(
    () => cupsPrintersForDocumentPrint(printersQuery.data?.printers ?? []),
    [printersQuery.data?.printers],
  );

  useEffect(() => {
    if (!open || selectedPrinter !== "" || printers.length === 0) return;
    const saved = localStorage.getItem(QUALITY_CASE_CUPS_PRINTER_STORAGE_KEY) ?? "";
    setSelectedPrinter(pickQualityCaseCupsPrinter(printers, saved));
  }, [open, printers, selectedPrinter]);

  const downloadMutation = useMutation({
    mutationFn: () => fetchQualityCaseTravelerHtml(caseId, investigationId),
    onSuccess: (html) => {
      const suffix = investigationId != null ? `-investigation-${String(investigationId)}` : "";
      downloadHtml(html, `quality-case-${String(caseId)}${suffix}-traveler.html`);
      showToast({ tone: "success", message: "Traveler downloaded." });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const printMutation = useMutation({
    mutationFn: () => {
      const printer = selectedPrinter.trim();
      if (printer === "") {
        throw new Error("Select the Case Management printer first.");
      }
      return printQualityCaseTraveler(caseId, { printer, investigationId });
    },
    onSuccess: (result) => {
      localStorage.setItem(QUALITY_CASE_CUPS_PRINTER_STORAGE_KEY, result.printer);
      setOpen(false);
      showToast({
        tone: "success",
        message: `Sent ${String(result.pageCount)}-page traveler to ${result.printer}.`,
      });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        variant="secondary"
        disabled={downloadMutation.isPending}
        onClick={() => downloadMutation.mutate()}
      >
        {downloadMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        Download traveler
      </Button>
      <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
        <Printer className="h-4 w-4" />
        Print traveler
      </Button>
      {open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 print:hidden">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-base font-semibold text-slate-900">Print investigation traveler</h2>
            <p className="mt-2 text-sm text-slate-600">
              Sends one front-and-back letter sheet per linked unit. Defaults to a Case Management queue when that
              printer is on the relay, otherwise the office letter printer. Print duplex, flip on the long edge.
            </p>
            <div className="mt-4">
              <Label htmlFor="quality-case-cups-printer" className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Printer
              </Label>
              <select
                id="quality-case-cups-printer"
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                value={selectedPrinter}
                disabled={printMutation.isPending || printersQuery.isLoading}
                onChange={(event) => setSelectedPrinter(event.target.value)}
              >
                {printers.length === 0 ? (
                  <option value="">{printersQuery.isLoading ? "Loading printers…" : "No letter printers found"}</option>
                ) : (
                  printers.map((printer) => (
                    <option key={printer.name} value={printer.name}>
                      {formatCupsPrinterOptionLabel(printer)}
                    </option>
                  ))
                )}
              </select>
              {printersQuery.isError ? (
                <p className="mt-2 text-sm text-rose-700">{(printersQuery.error as Error).message}</p>
              ) : null}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" disabled={printMutation.isPending} onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                disabled={printMutation.isPending || selectedPrinter.trim() === ""}
                onClick={() => printMutation.mutate()}
              >
                {printMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                Print traveler
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
